import type { ConfirmCheckoutPaymentServicePaymentIntentRequest } from 'types/payment-intent'
import type { GetReaderStatusResponse } from 'types/reader'

import { checkoutPaymentServiceAxiosClient } from './base'

const GET_CONNECTION_TOKEN_URL = '/key/terminal'
const POST_PAYMENT_REQUEST = '/payment-request'
const POST_CARD_REQUEST = '/card-request'
const SEND_PAYMENT_REQUEST_INVOICE = '/payment-request/invoice'
const UPDATE_PAYMENT_METHOD_URL = (paymentMethodId: string) => `/payment-method/${paymentMethodId}`

const READER_PROCESS_REQUEST = '/reader/process'
const READER_REFUND_REQUEST = '/reader/refund'
const READER_SAVE_CARD_REQUEST = '/reader/save-card'
const READER_CANCEL_REQUEST = '/reader/cancel'
const READER_STATUS = (readerId: string) => `/reader/${readerId}/status`
const READER_SIMULATE_SWIPE = '/reader/simulate-swipe'
const DELETE_SIMULATED_READER = (readerId) => `/reader/${readerId}`
const PAYMENT_INSTALLMENT = '/payment-installment'
const BANK_PAYMENT_PATH = '/bank-payment'

export const getTerminalConnectionToken = () =>
	checkoutPaymentServiceAxiosClient.get(GET_CONNECTION_TOKEN_URL)

/**
 * Creates a payment request and delivers it to the client, powering the
 * Text-To-Pay / Email-To-Pay tabs of Collect Payment as well as the AR and
 * payment request modals.
 *
 * @param data.customer_name - Name of the client the request is addressed to.
 * @param data.customer_phone - Client phone number, required when `send_method` includes `text`.
 * @param data.customer_email - Client email address, required when `send_method` includes `email`.
 * @param data.amount_due - Amount to collect, as a decimal dollar string (e.g. `'125.50'`).
 * @param data.practice_account_id - Stripe connected account ID (`acct_...`) of the collecting practice.
 * @param data.client_id - Practice-side client identifier, or `null` when not provided.
 * @param data.invoice_number - Practice-side invoice number, or `null` when not provided.
 * @param data.send_method - Delivery channels for the request, e.g. `['text']` or `['email']`.
 * @param data.note_to_client - Free-text note shown to the client, or `null`.
 * @param data.bypass_surcharge - When true, the resulting payment is not surcharged
 * (used for insurance, WEX or government agency payments).
 * @param data.source - Origin of the request; `'dashboard'` for admin-initiated requests.
 * @returns The Axios response whose `data` holds the created payment request,
 * including its `payment_request_id` and the generated payment link.
 */
export const createPaymentRequest = (data: CreatePaymentRequestBody) =>
	checkoutPaymentServiceAxiosClient.post(POST_PAYMENT_REQUEST, data)

/**
 * Creates a card request and delivers its link to the client, powering the Collect Card
 * Details tab of Collect Payment. The client opens the link and saves a card on file;
 * no payment is taken, so the payload carries no amount and no invoice number.
 *
 * @param data.customer_name - Name of the client the request is addressed to.
 * @param data.client_id - Practice-side client identifier the saved card is attached to.
 * @param data.delivery_method - Delivery channel, `'text'` or `'email'`. Defaults to `'text'` server-side.
 * @param data.customer_phone - Client phone number in E.164 (`+15555550123`), required when
 * `delivery_method` is `'text'`. This route passes it straight to Twilio without sanitizing it,
 * so it must already be normalized.
 * @param data.customer_email - Client email address, required when `delivery_method` is `'email'`.
 * @param data.practice_account_id - Stripe connected account ID (`acct_...`) of the requesting practice.
 * @param data.note_to_client - Free-text note delivered alongside the link, or `null`.
 * @param data.source - Origin of the request; `'dashboard'` for staff-initiated requests.
 * @returns The Axios response whose `data` holds `{ cardRequestId, status, deliveryMethod,
 * sentTo, url, shortUrl }` for the created request.
 */
export const createCardRequest = (data: CreateCardRequestBody) =>
	checkoutPaymentServiceAxiosClient.post(POST_CARD_REQUEST, data)

export const sendPaymentRequestInvoice = (data) =>
	checkoutPaymentServiceAxiosClient.post(SEND_PAYMENT_REQUEST_INVOICE, data)

export const updatePaymentMethod = (paymentMethodId, data) =>
	checkoutPaymentServiceAxiosClient.patch(UPDATE_PAYMENT_METHOD_URL(paymentMethodId), data)

interface ProcessReaderPaymentParams {
	readerId: string
	paymentIntentId: string
	saveCard: boolean
	promptDonation?: boolean
	promptTipping?: boolean
}

/**
 * Sends a payment to a terminal reader for card collection and processing.
 * Used by the server-driven terminal flow where the backend orchestrates the
 * reader interaction (collect → confirm → capture) via a single POST.
 *
 * @param params.readerId - Stripe Terminal reader ID (`tmr_...`).
 * @param params.paymentIntentId - Stripe Payment Intent ID (`pi_...`) created beforehand.
 * @param params.saveCard - Whether to save the card as a payment method after charge.
 * @param params.promptDonation - When boolean, forwarded as `prompt_donation` so the backend
 *   can trigger the donation prompt on the reader. Omitted for non-donation practices.
 * @param params.promptTipping - When boolean, forwarded as `prompt_tipping` so the backend
 *   can trigger the tipping prompt on the reader. Omitted for non-tipping practices.
 * @returns The reader process response from the payment service.
 */
export const processReaderPayment = ({
	readerId,
	paymentIntentId,
	saveCard,
	promptDonation,
	promptTipping
}: ProcessReaderPaymentParams) =>
	checkoutPaymentServiceAxiosClient.post(READER_PROCESS_REQUEST, {
		reader_id: readerId,
		payment_intent_id: paymentIntentId,
		save_payment_method: saveCard,
		// Forward the donation opt-in so the backend's handleDonationPrompt can fire; the handler
		// reads it from this /reader/process body, not from the PI created earlier.
		...(typeof promptDonation === 'boolean' && { prompt_donation: promptDonation }),
		...(typeof promptTipping === 'boolean' && { prompt_tipping: promptTipping })
	})
export const cancelReaderPayment = (readerId) =>
	checkoutPaymentServiceAxiosClient.post(READER_CANCEL_REQUEST, {
		reader_id: readerId
	})

/**
 * Request body for saving a card on a terminal reader without charging.
 * The backend creates a $0 SetupIntent and hands it off to the reader so the
 * client can present their card. Mirrors the payment-service SAVE_CARD_BODY_SCHEMA.
 */
export interface SaveReaderCardBody {
	/** Stripe Terminal reader ID (tmr_...) the card will be collected on. */
	reader_id: string
	/** Practice-facing client identifier used to resolve/create the Stripe customer. */
	client_id: string
	/** Stripe connected account ID of the practice (acct_...). */
	practice_account_id: string
	/** Optional PIMS client identifier, when the practice has a PIMS integration. */
	pims_client_id?: string | null
	/** Optional client name, used when creating a new Stripe customer. */
	customer_name?: string
	/** Optional client email, used when creating a new Stripe customer. */
	customer_email?: string
	/** Optional origin tag for auditing (e.g. 'dashboard'). */
	source?: string
	/** Optional extra metadata attached to the SetupIntent. */
	metadata?: Record<string, string>
}

/**
 * Response envelope returned by the save-card endpoint. The returned
 * `clientSecret` is what the frontend polls Stripe with to watch the
 * SetupIntent reach a terminal state.
 */
export interface SaveReaderCardResponse {
	success: boolean
	data: {
		/** Stripe SetupIntent ID (seti_...). */
		setupIntentId: string
		/** SetupIntent client secret, used for client-side polling via Stripe.js. */
		clientSecret: string
	}
}

/**
 * Saves a card on a Stripe Terminal reader without charging the client, for
 * recurring-payment setup when there is no card on file. The backend creates a
 * $0 SetupIntent, hands it off to the reader, and returns the SetupIntent's
 * client secret. Callers then poll Stripe directly (Stripe.js
 * `retrieveSetupIntent`) until the intent settles and read `payment_method`.
 *
 * @param body - Reader, client, and practice identifiers (plus optional customer details).
 *   `reader_id`, `client_id`, and `practice_account_id` are required.
 * @returns Success envelope whose `data` holds `setupIntentId` and `clientSecret`.
 */
export const saveReaderCard = (body: SaveReaderCardBody): Promise<SaveReaderCardResponse> =>
	checkoutPaymentServiceAxiosClient.post(READER_SAVE_CARD_REQUEST, body)

export const simulateReaderSwipe = (readerId, brand, funding, error) =>
	checkoutPaymentServiceAxiosClient.post(READER_SIMULATE_SWIPE, {
		reader_id: readerId,
		brand,
		funding,
		error
	})

export const deleteSimulatedReader = (readerId) =>
	checkoutPaymentServiceAxiosClient.delete(DELETE_SIMULATED_READER(readerId))

export const createInstallmentPlan = (data) =>
	checkoutPaymentServiceAxiosClient.post(PAYMENT_INSTALLMENT, data)

export const deleteInstallmentPlan = (subscriptionId) =>
	checkoutPaymentServiceAxiosClient.delete(`${PAYMENT_INSTALLMENT}/${subscriptionId}`)

export const updateInstallmentPlan = (subscriptionId, data) =>
	checkoutPaymentServiceAxiosClient.patch(`${PAYMENT_INSTALLMENT}/${subscriptionId}`, data)

export const createBankPayment = (data) =>
	checkoutPaymentServiceAxiosClient.post(BANK_PAYMENT_PATH, data)

/**
 * Confirms a Stripe Payment Intent through the checkout payment service. Used
 * by online card-collection flows where 3DS / next-action handling is needed
 * after the customer enters their payment method.
 *
 * @param req - Wrapper with URL params and body.
 *   - `params.paymentIntentId` - Stripe Payment Intent ID (`pi_...`).
 *   - `body.payment_method_id` - Stripe Payment Method ID (`pm_...`) to attach
 *     and confirm against.
 * @returns Axios response of `ConfirmCheckoutPaymentServicePaymentIntentResponse`
 *   — `{ success, data: { next_action, status, surcharge } }`. `next_action`
 *   carries the Stripe instruction (e.g. 3DS redirect) when further client
 *   action is required.
 */
export const confirmPaymentIntentByCheckoutPaymentService = (
	req: ConfirmCheckoutPaymentServicePaymentIntentRequest
) =>
	checkoutPaymentServiceAxiosClient.post(
		`/payment-intent/${req.params.paymentIntentId}/confirm`,
		req.body
	)

/**
 * Initiates a refund on a Stripe Terminal reader for a previously completed charge.
 * The reader will prompt the customer to present their card for the refund.
 * Used by the Canadian in-person refund flow (RefundCanadianPayment).
 *
 * @param readerId - The Stripe Terminal reader ID (e.g., "tmr_xxx") to process the refund on.
 * @param chargeId - The Stripe charge ID (e.g., "ch_xxx") of the original payment to refund.
 * @param amount - The refund amount in **dollars** (not cents). Partial refunds are supported.
 * @returns Axios response containing the reader object with `action.status: "in_progress"`.
 *   Poll `getReaderAction` to check for completion and obtain the refund ID.
 */
export const processReaderRefund = (readerId: string, chargeId: string, amount: number) =>
	checkoutPaymentServiceAxiosClient.post(READER_REFUND_REQUEST, {
		reader_id: readerId,
		charge_id: chargeId,
		amount,
		refund_application_fee: false,
		reverse_transfer: true
	})

/**
 * Retrieves the current action status of a Stripe Terminal reader.
 * Used to poll for completion of server-driven terminal operations (e.g., refund_payment).
 * After initiating a refund via `processReaderRefund`, poll this endpoint until
 * `action.status` is `succeeded` or `failed`.
 *
 * @param readerId - The Stripe Terminal reader ID (e.g., "tmr_xxx").
 * @returns `{ success, data: reader }` with `data.action.status` and, on success,
 *   `data.action.refund_payment.refund` (the Stripe refund ID).
 */
export const getReaderAction = (readerId: string): Promise<GetReaderStatusResponse> =>
	checkoutPaymentServiceAxiosClient.get(READER_STATUS(readerId))
